import * as THREE from 'three';
import {
  SKY_VERT,
  SKY_FRAG,
  computeSkyState,
  skyRadiance,
  sunTransmittance,
  applyDust,
  installDirectionalFog,
} from './shaders/sky.glsl.js';

/**
 * Atmosphere.
 *
 * One Preetham analytic sky drives four things that MUST agree with each other
 * or the scene instantly reads as a WebGL demo:
 *
 *   1. the sky dome you actually see,
 *   2. `scene.environment` — the PMREM-filtered IBL every PBR material samples
 *      for its specular response (without it, metal is black),
 *   3. `scene.fog`, whose colour is the measured horizon radiance so distant
 *      geometry dissolves into the *same* atmosphere rather than into grey soup,
 *   4. the sun/sky colours `Lighting.js` reads to drive the actual lights.
 *
 * Because (2) is expensive it is regenerated only when the sun moves — never
 * per frame.
 *
 * Art direction: late-afternoon golden hour over a dusty industrial district.
 * Low raking sun, heavy aerosol load, warm horizon, cool zenith.
 */

export const TUNING = {
  /** Degrees above the horizon. Golden hour lives at 8-14; below 8 goes red. */
  sunElevation: 13,
  /** Degrees. 0 = sun toward -Z. Puts the key light across the map, not behind you. */
  sunAzimuth: 118,

  /** Aerosol load. 2 = alpine air, 10 = smog. Dust district wants a lot. */
  turbidity: 9.0,
  /** Blue-scattering multiplier. Higher = more saturated zenith — but it also
   *  reddens the transmitted sun and pushes every shadow toward navy, because
   *  the sky IS the fill light. Balanced against `dustExtinction`, which warms
   *  the lower sky back up and takes the shade with it. */
  rayleigh: 2.4,
  /** Aerosol density. Drives the aureole around the sun, which grows *very*
   *  fast — past ~0.008 it eats the whole frame.
   *
   *  Cut from 0.0035 after measuring it: the aureole alone (before the disc or
   *  the halo term were added at all) exceeded 4.0 linear — pure white through
   *  ACES — out to 15 degrees from the sun. At the 25 degree ADS FOV that is a
   *  645 px radius of formless white, and it is what was eating the sun disc and
   *  dissolving midground geometry on the sun side. `mieDirectionalG` is raised
   *  to compensate: a tighter forward lobe keeps the aureole *bright* where it
   *  belongs, right against the disc, instead of spreading it over a quarter of
   *  the sky. Measured after: white confined to ~6 degrees. */
  mieCoefficient: 0.0004,
  /** Forward-scattering anisotropy. High = tight, bright halo around the sun. */
  mieDirectionalG: 0.9,

  /** Overall sky brightness going into ACES. */
  skyExposure: 1.0,
  /** Angular radius of the sun disc, degrees. Real sun is 0.265; golden hour
   *  reads bigger, but 0.62 was reading as a blob rather than a disc. */
  sunDiscRadius: 0.34,
  /** Linear radiance of the disc. Way over 1.0 on purpose — this is bloom food.
   *  It has to clear the aureole it sits in by a wide margin or the disc is
   *  simply not there; at 34 against an aureole of 30 it never was. */
  sunDiscIntensity: 110,
  /** Halo lobes are a thin limb bleed now — the aureole is carried by the Mie
   *  phase function, which is physics, rather than by an ad-hoc glow that clipped. */
  sunGlow: 0.35,

  /** Soft shoulder on the in-scattered sky term, applied before the sun disc.
   *  `skyKneeStart` sits above every colour the CPU sampler reads (its own
   *  ceiling is 1.5), so lights, fog and IBL are bit-for-bit unaffected — this
   *  only compresses the blown-out core around the sun, and keeps its gradient. */
  skyKneeStart: 1.6,
  skyKnee: 6.0,

  /** Suspended-dust layer sitting on the horizon.
   *  `dustExtinction` is what makes the horizon amber (it scrubs blue);
   *  `dustAmount` is the warm glow added back on top. */
  dustExtinction: 2.9,
  dustAmount: 0.5,
  /** Band tightness. Lower = the haze climbs higher up the sky. */
  dustFalloff: 4.2,
  dustTint: [1.0, 0.62, 0.3],

  /* The anti-solar half of the dust model. See applyDust() in sky.glsl.js for
   * why reddening is a sun-side phenomenon; the short version is that running
   * the warm set all the way round the compass dragged the sky behind the
   * player through the blue->amber crossover, and that crossover is green.
   * Measured olive band at 13 degrees elevation before: [137,155,126]. */

  /** Absorption spectrum away from the sun. Near-flat, tipped very slightly so
   *  the residue leans blue rather than green. */
  dustAbsorbAnti: [0.72, 0.74, 0.6],
  /** In-scatter tint away from the sun: the lilac-grey of the Belt of Venus.
   *  Measured anti-solar horizon after this change: [127,117,141]. */
  dustAntiTint: [0.62, 0.55, 0.74],
  /** Isotropic floor on the dust in-scatter, so anti-solar dust is lit at all. */
  dustAmbient: 0.38,
  /** How tightly the warm set is confined to the sun side. Lower = the gold
   *  wraps further round the horizon. */
  dustWarmFocus: 0.9,
  /** How much of the horizon radiance the ground haze inherits.
   *  Still drives `Sky.groundColor`, which Lighting reads for the hemisphere's
   *  down colour, the ambient hue and the bounce hue — do not touch without
   *  re-measuring the shaded-asphalt irradiance those values were solved for.
   *  It no longer paints the dome below the horizon; see `groundFalloff`. */
  groundMix: 0.62,
  groundTint: [1.0, 0.82, 0.66],

  /** Below-horizon fill on the visible dome.
   *
   *  Not a colour: the dome keeps the horizon's own radiance for that azimuth
   *  (warm toward the sun, lilac away from it) and lets it lose chroma and a
   *  little luminance with depression, the way terrain under haze does. What
   *  was there before was one flat, ring-averaged, `groundTint`-warmed colour
   *  crossfaded in over 5 degrees, which from a rooftop read as a hard orange
   *  band across the base of the skyline.
   *
   *  Depression in dir.y over which the fill settles. 0.34 ~ 20 degrees, which
   *  is below the skyline from any rooftop on this map, so the transition is
   *  never on screen as an edge. */
  groundFalloff: 0.34,
  /** Chroma kept at full depression. Distant haze is markedly less colourful
   *  than the sky it comes from; this is what stops the fill reading as paint. */
  groundDesat: 0.42,
  /** Luminance at full depression. Terrain in shadow under haze sits below the
   *  horizon glow — a fill as bright as the sky reads as a hole, not as ground. */
  groundGain: 0.58,

  /** FogExp2 density. 0.006 = clear-ish, 0.012 = thick dust.
   *
   *  Cut from 0.0088, which was destroying the thing fog exists to create.
   *  Transmittance at 200 m was 4.5%: a building at 90 m and one at 150 m
   *  arrived at the same value *and* the same hue, so the depth cue inverted
   *  into a depth eraser, and per-face light/shade separation — the entire
   *  read of a building's form — was gone past about 120 m.
   *
   *    density   T@100m   T@200m   T@300m
   *    0.0088    46%       4.5%     0.1%
   *    0.0040    85%      49%      24%
   *
   *  At 0.0040 a facade at 200 m still keeps half its own contrast, so its
   *  facets still separate, and the far end of the map still reads as air. */
  fogDensity: 0.004,
  /** Fog colour = horizon radiance * this. <1 keeps the distance from glowing
   *  brighter than the things in front of it. */
  fogGain: 0.72,
  /** Bias on the mid haze colour. Near-neutral now: the warm cast used to be
   *  applied globally here, and it is measured per-direction instead. */
  fogTint: [1.05, 1.0, 0.97],
  /** How much of the sampled chroma the haze keeps. Real distance haze is much
   *  less colourful than the sky it comes from, and a saturated fog colour is
   *  what made distant geometry read as burning rather than receding. */
  fogSaturation: 0.42,
  /** Multiplier on the warm-to-cool separation after desaturation. Saturation
   *  and directional contrast pull against each other; this buys the second one
   *  back without re-saturating the haze as a whole. */
  fogSplit: 1.35,

  /** How much IBL the world receives. Tuned with Lighting.TUNING.hemiIntensity. */
  envIntensity: 0.68,
  /** Luminance ceiling on the sky *as baked into the environment map*. Low on
   *  purpose: the sun's energy belongs to the directional light, not the IBL. */
  envRadianceClamp: 3.2,
  /** Aureole strength in the environment map only. */
  envSunGlow: 0.2,
  /** Saturation of the baked environment map, ramped by elevation — see the
   *  `satHorizon`/`satZenith` uniforms in sky.glsl.js.
   *
   *  Horizon stays near full: that band is warm, and it is what metals and
   *  vertical surfaces reflect. The zenith is pulled hard toward neutral,
   *  because up-facing surfaces integrate mostly zenith and a saturated
   *  golden-hour zenith is what turns asphalt navy.
   *
   *  Measured cosine-weighted irradiance for an up-facing normal:
   *    flat 1.00 -> b/r 2.99   flat 0.78 -> b/r 2.33   flat 0.60 -> b/r 1.92
   *    ramped     -> b/r 1.30, with the horizon band left essentially intact. */
  envSaturationHorizon: 0.95,
  /** Zenith pulled from 0.30 to 0.10. The ramp got up-facing irradiance from
   *  b/r 2.99 to 1.30, but 1.30 is still 30% blue-biased, and once the hemisphere
   *  fill was neutralised the IBL was the only cool term left — so shaded asphalt
   *  stayed navy in deep shadow (verified in an enclosed alley, where the ground
   *  sees sky and nothing else). The horizon band is untouched, so metals and
   *  vertical surfaces keep reflecting a real sky. */
  envSaturationZenith: 0.1,
  /** dir.y where the ramp starts and ends. */
  envSaturationRamp: [0.0, 0.6],

  skyRadius: 480,
};

/**
 * Elevation bands (degrees) and weights used to average the sky into the single
 * colour a hemisphere light can carry. Weighted toward the lower sky, which is
 * both what a ground-level surface mostly sees and where the warm dust lives.
 */
const SKY_FILL_BANDS = [
  [12, 0.3],
  [30, 0.3],
  [55, 0.24],
  [85, 0.16],
];

/** Luminance ceiling on a single sky sample. The Mie aureole makes the sky
 *  within ~15 degrees of the sun a hundred times brighter than the rest, and an
 *  unclamped sample leaves anything derived from it glowing white. */
const SAMPLE_CEIL = 1.5;
const LUMA = [0.2126, 0.7152, 0.0722];

function clampLuminance(v, ceil = SAMPLE_CEIL) {
  const lum = LUMA[0] * v.x + LUMA[1] * v.y + LUMA[2] * v.z;
  if (lum > ceil) v.multiplyScalar(ceil / lum);
  return v;
}

/** One dust-corrected horizon sample, `azOffset` radians around from the sun. */
function horizonSample(state, sunAz, azOffset, y = 0.03) {
  const c = Math.cos(azOffset);
  const s = Math.sin(azOffset);
  const v = new THREE.Vector3(
    sunAz.x * c - sunAz.z * s,
    y,
    sunAz.x * s + sunAz.z * c,
  ).normalize();
  const sample = skyRadiance(v, state, new THREE.Vector3());
  applyDust(sample, v.y, v.dot(state.sunDirection), TUNING);
  return clampLuminance(sample.multiplyScalar(TUNING.skyExposure));
}

/** Pulls a colour toward its own luminance. Keeps hue, drops chroma. */
function desaturate(v, amount) {
  const grey = LUMA[0] * v.x + LUMA[1] * v.y + LUMA[2] * v.z;
  return v.set(
    grey + (v.x - grey) * amount,
    grey + (v.y - grey) * amount,
    grey + (v.z - grey) * amount,
  );
}

/**
 * Measures the haze at three azimuths and turns them into what the fog chunk
 * needs: a mid colour, a warm and a cool ratio around it, and the exponent that
 * makes the two-colour blend pass through the *measured* 90-degree haze rather
 * than through the midpoint a straight lerp would invent.
 *
 * Fitting the exponent instead of hard-coding it is what keeps the fog agreeing
 * with the sky at three azimuths instead of two, which matters because the
 * warm-to-cool falloff around a low sun is nowhere near linear in cos.
 */
function measureFog(state) {
  const sunAz = new THREE.Vector3(state.sunDirection.x, 0, state.sunDirection.z).normalize();
  const warm = horizonSample(state, sunAz, 0);
  const cross = horizonSample(state, sunAz, Math.PI / 2);
  const cool = horizonSample(state, sunAz, Math.PI);

  // Where the 90-degree haze actually sits between the two ends, per channel,
  // weighted by how much that channel separates them at all.
  let num = 0;
  let den = 0;
  for (const ch of ['x', 'y', 'z']) {
    const span = warm[ch] - cool[ch];
    const w = Math.abs(span);
    if (w < 1e-5) continue;
    num += THREE.MathUtils.clamp((cross[ch] - cool[ch]) / span, 0.02, 0.98) * w;
    den += w;
  }
  const t = den > 0 ? num / den : 0.5;
  const focus = THREE.MathUtils.clamp(Math.log(t) / Math.log(0.5), 0.35, 4);

  desaturate(warm, TUNING.fogSaturation);
  desaturate(cool, TUNING.fogSaturation);

  // Push the two ends apart again around their common mid: desaturating both
  // collapses the warm/cool separation along with the chroma, and that
  // separation is the entire point of the exercise.
  const mid = warm.clone().add(cool).multiplyScalar(0.5);
  warm.sub(mid).multiplyScalar(TUNING.fogSplit).add(mid);
  cool.sub(mid).multiplyScalar(TUNING.fogSplit).add(mid);
  warm.max(new THREE.Vector3(1e-4, 1e-4, 1e-4));
  cool.max(new THREE.Vector3(1e-4, 1e-4, 1e-4));

  mid.copy(warm).add(cool).multiplyScalar(0.5);
  const tinted = mid
    .clone()
    .multiplyScalar(TUNING.fogGain)
    .multiply(new THREE.Vector3(...TUNING.fogTint));

  return {
    color: tinted,
    focus,
    warm: [warm.x / mid.x, warm.y / mid.y, warm.z / mid.z],
    cool: [cool.x / mid.x, cool.y / mid.y, cool.z / mid.z],
    sunDirection: state.sunDirection,
  };
}

/**
 * The fog chunk compiles its colours in as literals, so it has to be installed
 * before the first lit material compiles — same constraint, and the same
 * solution, as the cascade patch in Lighting.js. `main()` builds Sky before the
 * level, but AssetForge runs before *both*, so module load is the only place
 * that is unconditionally early enough.
 */
installDirectionalFog(
  measureFog(
    computeSkyState({
      sunElevation: TUNING.sunElevation,
      sunAzimuth: TUNING.sunAzimuth,
      turbidity: TUNING.turbidity,
      rayleigh: TUNING.rayleigh,
      mieCoefficient: TUNING.mieCoefficient,
      mieDirectionalG: TUNING.mieDirectionalG,
    }),
  ),
);

export class Sky {
  constructor(ctx) {
    this.ctx = ctx;

    /** @type {THREE.Vector3} normalised, points *from the world toward the sun* */
    this.sunDirection = new THREE.Vector3(0, 1, 0);
    /** @type {THREE.Color} linear-space colour of direct sunlight */
    this.sunColor = new THREE.Color(1, 1, 1);
    /** @type {THREE.Color} linear-space colour of the upper sky (cool fill) */
    this.skyColor = new THREE.Color(1, 1, 1);
    /** @type {THREE.Color} linear-space colour at the horizon (fog / haze) */
    this.horizonColor = new THREE.Color(1, 1, 1);
    /** @type {THREE.Color} linear-space bounce colour off the dusty ground */
    this.groundColor = new THREE.Color(1, 1, 1);
    /** Relative brightness of direct sunlight, 0..1-ish. Lighting scales by this. */
    this.sunLuminance = 1;

    this.uniforms = {
      sunDirection: { value: new THREE.Vector3(0, 1, 0) },
      sunE: { value: 100 },
      betaR: { value: new THREE.Vector3() },
      betaM: { value: new THREE.Vector3() },
      mieG: { value: TUNING.mieDirectionalG },
      skyExposure: { value: TUNING.skyExposure },
      sunDiscCos: { value: Math.cos(THREE.MathUtils.degToRad(TUNING.sunDiscRadius)) },
      sunDiscIntensity: { value: TUNING.sunDiscIntensity },
      sunGlow: { value: TUNING.sunGlow },
      groundFalloff: { value: TUNING.groundFalloff },
      groundDesat: { value: TUNING.groundDesat },
      groundGain: { value: TUNING.groundGain },
      // The visible dome continues the horizon downward; only the env copy
      // keeps the old flat fill. See sky.glsl.js for the irradiance table.
      groundFlat: { value: 0 },
      groundColor: { value: new THREE.Vector3() },
      dustColor: { value: new THREE.Vector3() },
      dustAntiColor: { value: new THREE.Vector3() },
      dustAbsorbAnti: { value: new THREE.Vector3() },
      dustAmbient: { value: TUNING.dustAmbient },
      dustWarmFocus: { value: TUNING.dustWarmFocus },
      dustAmount: { value: TUNING.dustAmount },
      dustExtinction: { value: TUNING.dustExtinction },
      dustFalloff: { value: TUNING.dustFalloff },
      skyKneeStart: { value: TUNING.skyKneeStart },
      skyKnee: { value: TUNING.skyKnee },
      radianceClamp: { value: 1e6 }, // the visible dome is unclamped
      // The visible dome keeps full saturation at both ends of the ramp.
      satHorizon: { value: 1 },
      satZenith: { value: 1 },
      satRamp: { value: new THREE.Vector2(0, 1) },
    };

    // The environment map is baked from the same shader with the sun disc removed
    // and the aureole clamped: the sun's direct energy already lives in the
    // directional light, and double-counting it in the IBL is what turns a
    // golden-hour scene into flat milk.
    this.envUniforms = {};
    for (const k of Object.keys(this.uniforms)) this.envUniforms[k] = this.uniforms[k];
    this.envUniforms.sunDiscIntensity = { value: 0 };
    // Legacy flat below-horizon fill, environment map only — the light rig's
    // measured irradiance depends on it. See the table in sky.glsl.js.
    this.envUniforms.groundFlat = { value: 1 };
    this.envUniforms.sunGlow = { value: TUNING.envSunGlow };
    this.envUniforms.radianceClamp = { value: TUNING.envRadianceClamp };
    this.envUniforms.satHorizon = { value: TUNING.envSaturationHorizon };
    this.envUniforms.satZenith = { value: TUNING.envSaturationZenith };
    this.envUniforms.satRamp = { value: new THREE.Vector2(...TUNING.envSaturationRamp) };

    this._buildDome();
    this._buildEnvSource();

    this.pmrem = new THREE.PMREMGenerator(ctx.renderer);
    this.pmrem.compileCubemapShader();
    this.envTarget = null;

    this.refresh();

    // The dome is centred on the camera every frame so it can never be escaped.
    ctx.engine.registerUpdate(() => this._follow(), 'fx');
  }

  /* ------------------------------- geometry -------------------------------- */

  _buildDome() {
    const material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: SKY_VERT,
      fragmentShader: SKY_FRAG,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: true,
      fog: false,
      toneMapped: true,
    });

    this.mesh = new THREE.Mesh(new THREE.SphereGeometry(TUNING.skyRadius, 48, 32), material);
    this.mesh.name = 'sky';
    this.mesh.frustumCulled = false;
    // Drawn last among opaques: every pixel already covered by world geometry is
    // rejected by the depth test, so the dome costs only the sky it actually shows.
    this.mesh.renderOrder = 1000;
    this.mesh.matrixAutoUpdate = true;
    this.ctx.scene.add(this.mesh);
  }

  /** A private one-object scene the PMREM generator renders from. */
  _buildEnvSource() {
    this._envScene = new THREE.Scene();
    const material = new THREE.ShaderMaterial({
      // Shares every scattering uniform with the dome, so the IBL can never
      // disagree with the sky you see — only the sun disc and the ceiling differ.
      uniforms: this.envUniforms,
      vertexShader: SKY_VERT,
      fragmentShader: SKY_FRAG,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
    });
    this._envMesh = new THREE.Mesh(new THREE.BoxGeometry(20, 20, 20), material);
    this._envMesh.frustumCulled = false;
    this._envScene.add(this._envMesh);
  }

  /* -------------------------------- driving -------------------------------- */

  /**
   * Move the sun. Recomputes every derived colour, rebuilds the environment map
   * and pokes Lighting so the light rig follows. Not cheap — call on changes only.
   */
  setSun(elevationDeg, azimuthDeg) {
    TUNING.sunElevation = elevationDeg;
    TUNING.sunAzimuth = azimuthDeg;
    this.refresh();
    this.ctx.lighting?.onSkyChanged?.();
  }

  refresh() {
    const state = computeSkyState({
      sunElevation: TUNING.sunElevation,
      sunAzimuth: TUNING.sunAzimuth,
      turbidity: TUNING.turbidity,
      rayleigh: TUNING.rayleigh,
      mieCoefficient: TUNING.mieCoefficient,
      mieDirectionalG: TUNING.mieDirectionalG,
    });
    this.state = state;
    this.sunDirection.copy(state.sunDirection);

    this._sampleColors(state);
    this.fogMeasurement = measureFog(state);
    this._pushUniforms(state);
    this._buildEnvironment();
    this._applyFog();
  }

  /**
   * Evaluate the *same* radiance function the shader runs, on the CPU, to get
   * the numbers the light rig and the fog need. This is what keeps sky and sun
   * locked together.
   */
  _sampleColors(state) {
    const v = new THREE.Vector3();
    const sunAz = new THREE.Vector3(state.sunDirection.x, 0, state.sunDirection.z).normalize();
    const y = 0.03;

    // Average the whole horizon ring rather than one direction, because the
    // Mie aureole makes the sky within ~15 degrees of the sun literally a
    // hundred times brighter than the rest — averaging it in raw would leave
    // the fog glowing white. Each sample is ceiling-clamped for the same reason,
    // while still letting the sun side pull the average warm.
    const CEIL = SAMPLE_CEIL;
    const horizon = new THREE.Vector3();
    const RING = 12;
    for (let i = 0; i < RING; i++) {
      // Clamped by *luminance*, not per channel: a per-channel clamp would
      // flatten the amber sun side to neutral white and the haze would lose its
      // warmth. (horizonSample applies the dust, the exposure and the ceiling.)
      horizon.addScaledVector(horizonSample(state, sunAz, (i / RING) * Math.PI * 2, y), 1 / RING);
    }

    // Sky fill.
    //
    // This is NOT the zenith colour. A hemisphere light stands in for the whole
    // dome, and most of the dome an outdoor surface actually sees is the lower
    // sky — which at golden hour is amber, not blue. Sampling only the zenith is
    // why naive golden-hour scenes end up with navy shadows that look like
    // moonlight: the fill ends up bluer than any real skylight ever is.
    const sky = new THREE.Vector3();
    for (const [elevation, weight] of SKY_FILL_BANDS) {
      const cy = Math.cos(THREE.MathUtils.degToRad(elevation));
      const sy = Math.sin(THREE.MathUtils.degToRad(elevation));
      // Two samples per band, on and across the sun azimuth, so the fill picks up
      // the warm side without being dominated by the aureole.
      for (const [ax, az] of [
        [sunAz.x, sunAz.z],
        [-sunAz.z, sunAz.x],
      ]) {
        const s = skyRadiance(v.set(ax * cy, sy, az * cy).normalize(), state, new THREE.Vector3());
        applyDust(s, v.y, v.dot(state.sunDirection), TUNING);
        const lum = 0.2126 * s.x + 0.7152 * s.y + 0.0722 * s.z;
        if (lum > CEIL) s.multiplyScalar(CEIL / lum);
        sky.addScaledVector(s, weight * 0.5);
      }
    }
    sky.multiplyScalar(TUNING.skyExposure);

    this.horizonColor.setRGB(horizon.x, horizon.y, horizon.z, THREE.LinearSRGBColorSpace);
    this.skyColor.setRGB(sky.x, sky.y, sky.z, THREE.LinearSRGBColorSpace);

    // Ground haze under the horizon line.
    const ground = horizon
      .clone()
      .multiplyScalar(TUNING.groundMix)
      .multiply(new THREE.Vector3(...TUNING.groundTint));
    this.groundColor.setRGB(ground.x, ground.y, ground.z, THREE.LinearSRGBColorSpace);
    this._groundVec = ground;
    this._horizonVec = horizon;

    // Direct sunlight: transmittance along the sun ray. At this elevation it is a
    // deep amber, which is exactly the point.
    const fex = sunTransmittance(state);
    this.sunLuminance = Math.max(1e-4, 0.2126 * fex.x + 0.7152 * fex.y + 0.0722 * fex.z);
    const peak = Math.max(fex.x, fex.y, fex.z, 1e-4);
    this.sunColor.setRGB(fex.x / peak, fex.y / peak, fex.z / peak, THREE.LinearSRGBColorSpace);
  }

  _pushUniforms(state) {
    const u = this.uniforms;
    u.sunDirection.value.copy(state.sunDirection);
    u.sunE.value = state.sunE;
    u.betaR.value.copy(state.betaR);
    u.betaM.value.copy(state.betaM);
    u.mieG.value = state.mieG;
    u.skyExposure.value = TUNING.skyExposure;
    u.sunDiscCos.value = Math.cos(THREE.MathUtils.degToRad(TUNING.sunDiscRadius));
    u.sunDiscIntensity.value = TUNING.sunDiscIntensity;
    u.sunGlow.value = TUNING.sunGlow;
    u.dustAmount.value = TUNING.dustAmount;
    u.dustExtinction.value = TUNING.dustExtinction;
    u.dustFalloff.value = TUNING.dustFalloff;
    u.dustColor.value.set(...TUNING.dustTint);
    u.dustAntiColor.value.set(...TUNING.dustAntiTint);
    u.dustAbsorbAnti.value.set(...TUNING.dustAbsorbAnti);
    u.dustAmbient.value = TUNING.dustAmbient;
    u.dustWarmFocus.value = TUNING.dustWarmFocus;
    u.skyKneeStart.value = TUNING.skyKneeStart;
    u.skyKnee.value = TUNING.skyKnee;
    u.groundFalloff.value = TUNING.groundFalloff;
    u.groundDesat.value = TUNING.groundDesat;
    u.groundGain.value = TUNING.groundGain;
    u.groundColor.value.copy(this._groundVec);
    this.envUniforms.sunGlow.value = TUNING.envSunGlow;
    this.envUniforms.radianceClamp.value = TUNING.envRadianceClamp;
    this.envUniforms.satHorizon.value = TUNING.envSaturationHorizon;
    this.envUniforms.satZenith.value = TUNING.envSaturationZenith;
    this.envUniforms.satRamp.value.set(...TUNING.envSaturationRamp);
  }

  /* ------------------------------ environment ------------------------------- */

  _buildEnvironment() {
    const { renderer, scene, viewScene } = this.ctx;

    const prevTarget = renderer.getRenderTarget();
    const old = this.envTarget;

    this.envTarget = this.pmrem.fromScene(this._envScene, 0, 0.1, 100);
    renderer.setRenderTarget(prevTarget);

    scene.environment = this.envTarget.texture;
    scene.environmentIntensity = TUNING.envIntensity;
    // The first-person viewmodel lives in its own scene; without this the gun
    // has no specular response at all and reads as plastic.
    if (viewScene) {
      viewScene.environment = this.envTarget.texture;
      viewScene.environmentIntensity = TUNING.envIntensity;
    }

    old?.dispose();
  }

  /**
   * `scene.fog` carries the density and the *mid* haze colour; the warm/cool
   * swing around that colour lives in the patched fog chunk, because a FogExp2
   * has exactly one colour and this scene needs the haze to disagree with
   * itself across a single frame. See installDirectionalFog().
   */
  _applyFog() {
    const m = this.fogMeasurement;
    const c = m.color;
    const fog = this.ctx.scene.fog;
    if (fog && fog.isFogExp2) {
      fog.color.setRGB(c.x, c.y, c.z, THREE.LinearSRGBColorSpace);
      fog.density = TUNING.fogDensity;
    } else {
      const color = new THREE.Color().setRGB(c.x, c.y, c.z, THREE.LinearSRGBColorSpace);
      this.ctx.scene.fog = new THREE.FogExp2(color, TUNING.fogDensity);
    }

    // A no-op unless the sun actually moved: the chunk compares signatures. When
    // it does move, every material that already compiled has the old literals
    // baked in and has to be rebuilt.
    if (installDirectionalFog(m) && this._fogChunkLive) {
      this.ctx.scene.traverse((o) => {
        const mat = o.material;
        if (!mat) return;
        if (Array.isArray(mat)) mat.forEach((x) => (x.needsUpdate = true));
        else mat.needsUpdate = true;
      });
    }
    this._fogChunkLive = true;
  }

  /* --------------------------------- frame ---------------------------------- */

  _follow() {
    this.mesh.position.copy(this.ctx.camera.position);
  }

  dispose() {
    this.ctx.scene.remove(this.mesh);
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
    this._envMesh.geometry.dispose();
    this._envMesh.material.dispose();
    this.envTarget?.dispose();
    this.pmrem.dispose();
  }
}
